import { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config/api.js';

const errorMessages = {
  ID_MISSING: '缺少 PI 链接编号',
  LINK_NOT_FOUND: 'PI 链接不存在或已被删除',
  LINK_CLOSED: 'PI 链接已关闭',
  LINK_EXPIRED: 'PI 链接已过期',
  PI_DATA_INVALID: 'PI 数据暂时无法读取'
};

const text = (value, fallback = '-') => {
  const normalized = String(value ?? '').trim();
  return normalized || fallback;
};

const money = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '-';
  if (/^[A-Z]{3}\s/i.test(raw) || raw.startsWith('$')) return raw;
  const numeric = Number(raw.replace(/,/g, ''));
  if (Number.isFinite(numeric)) return `$${numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return raw;
};

const formatDate = (value) => {
  if (!value) return '-';
  if (typeof value === 'string' && Number.isNaN(Number(value))) return value;
  const date = new Date(Number(value));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
};

const formatTimestamp = (value) => {
  if (!value) return '-';
  const date = new Date(Number(value));
  if (!Number.isNaN(date.getTime())) return date.toLocaleString();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

function InfoRows({ title, rows }) {
  return (
    <section className="public-pi-section">
      <h2>{title}</h2>
      <dl className="public-pi-info-list">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{text(value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function PublicPiPage() {
  const id = useMemo(() => new URLSearchParams(window.location.search).get('id') || '', []);
  const [state, setState] = useState({ status: id ? 'loading' : 'error', data: null, error: id ? '' : 'ID_MISSING' });
  const [actionStatus, setActionStatus] = useState('');

  useEffect(() => {
    document.title = id ? `PI ${id} - Geeklly` : 'PI - Geeklly';
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();

    async function loadPi() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/get-pi?id=${encodeURIComponent(id)}`, {
          signal: controller.signal,
          cache: 'no-store'
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.error) {
          throw new Error(data.error || `HTTP_${response.status}`);
        }
        setState({ status: 'ready', data, error: '' });
      } catch (error) {
        if (error.name === 'AbortError') return;
        setState({ status: 'error', data: null, error: error.message || 'LOAD_FAILED' });
      }
    }

    loadPi();
    return () => controller.abort();
  }, [id]);

  if (state.status === 'loading') {
    return (
      <main className="public-pi-page public-pi-center">
        <div className="public-pi-status">正在读取 PI...</div>
      </main>
    );
  }

  if (state.status === 'error') {
    return (
      <main className="public-pi-page public-pi-center">
        <div className="public-pi-error">
          <p>{errorMessages[state.error] || 'PI 链接暂时无法打开'}</p>
          <span>{id ? `ID: ${id}` : '请确认链接是否完整'}</span>
        </div>
      </main>
    );
  }

  const pi = state.data;
  const seller = pi.seller || {};
  const buyer = pi.buyer || {};
  const authority = pi.authority || {};
  const products = Array.isArray(pi.products) ? pi.products : [];
  const currency = text(pi.currency, 'USD');

  const sendPiEvent = async (eventType) => {
    setActionStatus('syncing');
    try {
      await fetch(`${API_BASE_URL}/api/pi-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          piId: id,
          eventType,
          customer: buyer.name || buyer.company || pi.name,
          product: products[0]?.name || products[0]?.productName || pi.product,
          waAccountId: pi.waAccountId,
          timestamp: new Date().toISOString()
        })
      });
      setActionStatus('synced');
    } catch (error) {
      setActionStatus('offline');
    }
  };

  return (
    <main className="public-pi-page">
      <article className="public-pi-document">
        <header className="public-pi-header">
          <div>
            {pi.logo ? <img className="public-pi-logo" src={pi.logo} alt={seller.company || 'Seller logo'} /> : <div className="public-pi-logo-placeholder">G</div>}
          </div>
          <div className="public-pi-title-block">
            <p>PROFORMA INVOICE</p>
            <h1>{text(pi.no || pi.id, 'PI')}</h1>
            <span>Issue Date: {text(pi.date)}</span>
            <span>Valid Until: {text(pi.valid || formatDate(pi._meta?.valid_until))}</span>
            <span>Currency: {currency}</span>
            <span>Version: {text(pi.versionNo || pi.version, 'V1')}</span>
          </div>
        </header>

        <div className="public-pi-grid">
          <InfoRows
            title="Seller"
            rows={[
              ['Company', seller.company],
              ['Contact', seller.contact],
              ['Email', seller.email],
              ['Tel', seller.tel],
              ['Address', seller.address],
              ['Website', seller.website],
              ['Business Registration No.', seller.registrationNo]
            ]}
          />
          <InfoRows
            title="Buyer"
            rows={[
              ['Company', buyer.company],
              ['Name', buyer.name],
              ['Country', buyer.country],
              ['Email', buyer.email],
              ['Tel', buyer.tel],
              ['Address', buyer.address]
            ]}
          />
        </div>

        <section className="public-pi-section public-pi-products">
          <h2>Products</h2>
          <div className="public-pi-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>SKU</th>
                  <th>HS Code</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Amount</th>
                  <th>Packaging</th>
                  <th>Lead Time</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => (
                  <tr key={`${product.name || 'product'}-${index}`}>
                    <td>
                      <strong>{text(product.name || product.productName)}</strong>
                      <span>{text(product.description, '')}</span>
                    </td>
                    <td>{text(product.sku)}</td>
                    <td>{text(product.hsCode || product.hs_code)}</td>
                    <td>{text(product.qty || product.quantity)}</td>
                    <td>{money(product.price || product.unitPrice)}</td>
                    <td>{money(product.amount || product.total)}</td>
                    <td>{text(product.packaging)}</td>
                    <td>{text(product.leadTime || product.lead_time)}</td>
                  </tr>
                ))}
                {!products.length ? (
                  <tr>
                    <td colSpan="8">No products</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <div className="public-pi-grid public-pi-bottom-grid">
          <InfoRows
            title="Terms"
            rows={[
              ['Payment Terms', pi.paymentTerms],
              ['Deposit Ratio', pi.depositRatio ? `${pi.depositRatio}%` : ''],
              ['Balance Payment', pi.balancePayment],
              ['Trade Terms', pi.tradeTerms],
              ['Port of Loading', pi.portLoading],
              ['Destination', pi.destination],
              ['Shipping Method', pi.shippingMethod],
              ['Delivery Time', pi.deliveryTime]
            ]}
          />
          <section className="public-pi-section public-pi-totals">
            <h2>Total</h2>
            <dl>
              <div><dt>Subtotal</dt><dd>{money(pi.subtotal)}</dd></div>
              <div><dt>Shipping</dt><dd>{money(pi.shipping)}</dd></div>
              <div><dt>Other Fees</dt><dd>{money(pi.otherFees)}</dd></div>
              <div><dt>Discount</dt><dd>{money(pi.discount)}</dd></div>
              <div className="public-pi-grand-total"><dt>Total Payable</dt><dd>{money(pi.totalPayable || pi.grandTotal)}</dd></div>
              <div><dt>Deposit Amount</dt><dd>{money(pi.depositAmount)}</dd></div>
              <div><dt>Balance Amount</dt><dd>{money(pi.balanceAmount)}</dd></div>
            </dl>
          </section>
        </div>

        <div className="public-pi-grid public-pi-bottom-grid">
          <InfoRows
            title="Bank"
            rows={[
              ['Bank Name', pi.bank?.name],
              ['Account Name', pi.bank?.accountName],
              ['Account No.', pi.bank?.accountNo],
              ['SWIFT', pi.bank?.swift],
              ['Bank Address', pi.bank?.address]
            ]}
          />
          <section className="public-pi-section">
            <h2>Authority</h2>
            <dl className="public-pi-info-list">
              <div><dt>Sales Manager</dt><dd>{text(authority.salesManager)}</dd></div>
              <div><dt>Signed Date</dt><dd>{text(authority.signedDate)}</dd></div>
              <div><dt>Signature Source</dt><dd>{text(authority.signatureSource)}</dd></div>
              <div><dt>Signature Timestamp</dt><dd>{formatTimestamp(authority.signedTimestamp)}</dd></div>
              <div>
                <dt>Authorized Signature</dt>
                <dd>
                  {authority.signatureUrl ? (
                    <img className="public-pi-signature-img" src={authority.signatureUrl} alt="Authorized Signature" />
                  ) : (
                    text('-')
                  )}
                </dd>
              </div>
              <div>
                <dt>Company Stamp</dt>
                <dd>
                  {authority.stampUrl ? (
                    <img className="public-pi-signature-img" src={authority.stampUrl} alt="Company Stamp" />
                  ) : (
                    text('-')
                  )}
                </dd>
              </div>
            </dl>
          </section>
        </div>

        <section className="public-pi-section public-pi-actions">
          <div>
            <h2>Customer Action</h2>
            <p>Choose the next step for this Proforma Invoice. Your action will notify the seller.</p>
          </div>
          <div className="public-pi-action-grid">
            <button onClick={() => sendPiEvent('pi_accept')}>Accept PI</button>
            <button onClick={() => sendPiEvent('revision_request')}>Request Revision</button>
            <button onClick={() => sendPiEvent('deposit_click')}>Pay Deposit</button>
            <button onClick={() => sendPiEvent('pdf_download')}>Download PDF</button>
          </div>
          {actionStatus ? <span className="public-pi-action-status">{actionStatus === 'synced' ? 'Action recorded' : actionStatus === 'offline' ? 'Action saved locally, seller sync pending' : 'Syncing action...'}</span> : null}
        </section>

        <section className="public-pi-section">
          <h2>Notes</h2>
          <p className="public-pi-notes">{text(pi.notes, 'No additional notes')}</p>
        </section>
      </article>
    </main>
  );
}